/**
 * The tenant directory, and the two writes that change a row in it.
 *
 * @module components/platform/useTenantDirectory
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { parseAddressList } from "@/components/shared/address-list";
import type {
  ProgrammePlatformClient,
  ReissueTenantInvitationInput,
  TenantOnboardingSummary,
} from "@/platform";
import { begin } from "@/utils/begin";

export interface ReissueDraft {
  email: string;
  alternateEmails: string;
  displayName: string;
}

export interface TenantDirectoryState {
  tenants: readonly TenantOnboardingSummary[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** True while a reissue is in flight; the form waits on it. */
  reissuing: boolean;
  reissueError: string | null;
  /** Resolves true when the invitation went, so the form can close itself. */
  reissue: (tenantId: string, draft: ReissueDraft) => Promise<boolean>;
  clearReissueError: () => void;
  /** The tenant whose entitlement is being written, or null. */
  entitlementSavingTenantId: string | null;
  entitlementError: { tenantId: string; message: string } | null;
  setPublicCurriculumApiEnabled: (tenantId: string, enabled: boolean) => void;
}

/**
 * @param client - The platform client to read and write through.
 * @returns The directory, and what the last write said.
 *
 * @example
 * ```tsx
 * const directory = useTenantDirectory(client);
 * ```
 */
export function useTenantDirectory(client: ProgrammePlatformClient): TenantDirectoryState {
  const [tenants, setTenants] = useState<readonly TenantOnboardingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reissuing, setReissuing] = useState(false);
  const [reissueError, setReissueError] = useState<string | null>(null);
  const [entitlementSavingTenantId, setEntitlementSavingTenantId] = useState<string | null>(null);
  const [entitlementError, setEntitlementError] = useState<{
    tenantId: string;
    message: string;
  } | null>(null);
  const entitlementGenerations = useRef<Map<string, number>>(new Map());

  // Fetch and publish, with no synchronous prologue, so the mount effect can
  // call it without setting state in its own body.
  const fetchTenants = useCallback(
    () =>
      begin(() => client.listTenantOnboarding())
        .then((result) => {
          setTenants(result);
          setError(null);
        })
        .catch((cause: unknown) => {
          setError(cause instanceof Error ? cause.message : "Unable to load tenant directory");
        })
        .finally(() => {
          setLoading(false);
        }),
    [client],
  );

  // The refresh an event handler wants: show the spinner and drop the stale
  // error straight away, then fetch. `loading` already starts true, so the
  // mount path needs none of it.
  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    await fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const replace = (summary: TenantOnboardingSummary): void => {
    setTenants((current) => current.map((entry) => (entry.id === summary.id ? summary : entry)));
  };

  const reissue = async (tenantId: string, draft: ReissueDraft): Promise<boolean> => {
    const email = draft.email.trim();
    if (!email) {
      setReissueError("An administrator email is required.");
      return false;
    }
    setReissuing(true);
    setReissueError(null);
    const alternateEmails = parseAddressList(draft.alternateEmails);
    const displayName = draft.displayName.trim();
    const input: ReissueTenantInvitationInput = {
      tenantId,
      email: email.toLowerCase(),
      ...(alternateEmails.length ? { alternateEmails } : {}),
      ...(displayName ? { displayName } : {}),
    };
    try {
      replace(await client.reissueTenantInvitation(input));
      return true;
    } catch (cause) {
      setReissueError(cause instanceof Error ? cause.message : "Unable to reissue the invitation");
      return false;
    } finally {
      setReissuing(false);
    }
  };

  const clearReissueError = useCallback((): void => {
    setReissueError(null);
  }, []);

  const setPublicCurriculumApiEnabled = (tenantId: string, enabled: boolean): void => {
    const nextGeneration = (entitlementGenerations.current.get(tenantId) ?? 0) + 1;
    entitlementGenerations.current.set(tenantId, nextGeneration);
    setEntitlementSavingTenantId(tenantId);
    setEntitlementError(null);
    client
      .setTenantPublicCurriculumApiEntitlement({ tenantId, enabled })
      .then((decision) => {
        if (entitlementGenerations.current.get(tenantId) === nextGeneration) {
          setTenants((current) =>
            current.map((entry) =>
              entry.id === tenantId
                ? { ...entry, publicCurriculumApiEnabled: decision.enabled }
                : entry,
            ),
          );
        }
      })
      .catch((cause: unknown) => {
        if (entitlementGenerations.current.get(tenantId) === nextGeneration) {
          setEntitlementError({
            tenantId,
            message:
              cause instanceof Error
                ? cause.message
                : "Unable to change public curriculum API access",
          });
        }
      })
      .finally(() => {
        if (entitlementGenerations.current.get(tenantId) === nextGeneration) {
          setEntitlementSavingTenantId((current) => (current === tenantId ? null : current));
        }
      });
  };

  return {
    tenants,
    loading,
    error,
    reload,
    reissuing,
    reissueError,
    reissue,
    clearReissueError,
    entitlementSavingTenantId,
    entitlementError,
    setPublicCurriculumApiEnabled,
  };
}
